import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../components/Logo';
import Card from '../components/Card';
import toast from 'react-hot-toast';
import Loader from '../components/Loader';

// Assets
import staticServices from '../data/staticServices.json';
import pruebaImg from '../images/prueba.webp';
import donateImg from '../images/donate.webp';

const imgMap = {
    'prueba.webp': pruebaImg,
    'donate.webp': donateImg
};

const ServicesPage = () => {
    const [events, setEvents] = useState([]);
    const [activeTitle, setActiveTitle] = useState('Servicios');
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    // Combine events and static services for easy tracking.
    // useMemo: sin él este arreglo se recrea en cada render y el useEffect de abajo
    // vuelve a colgar y descolgar el listener de scroll cada vez.
    const allCards = useMemo(() => [
        ...events.map(e => ({ ...e, isEvent: true })),
        ...staticServices.map(s => ({ ...s, isEvent: false, image_url: imgMap[s.imagePath] }))
    ], [events]);

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollRef.current) return;
            const carousel = scrollRef.current;
            const scrollLeft = carousel.scrollLeft;
            const items = carousel.querySelectorAll('.snap-center');
            
            if (items.length === 0) return;

            // Find the item closest to the center of the carousel
            const carouselCenter = scrollLeft + carousel.offsetWidth / 2;
            let closestTitle = activeTitle;
            let closestIndex = 0;
            let minDistance = Infinity;

            items.forEach((item, index) => {
                const itemCenter = item.offsetLeft + item.offsetWidth / 2;
                const distance = Math.abs(carouselCenter - itemCenter);
                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                    const card = allCards[index];
                    if (card?.isEvent) {
                        closestTitle = 'Eventos';
                    } else {
                        closestTitle = card?.pageTitle || card?.title || 'Servicios';
                    }
                }
            });

            if (closestTitle !== activeTitle) {
                setActiveTitle(closestTitle);
            }
            setActiveIndex(closestIndex);
        };

        const carousel = scrollRef.current;
        if (carousel) {
            carousel.addEventListener('scroll', handleScroll, { passive: true });
            // Initial check
            handleScroll();
        }
        return () => carousel?.removeEventListener('scroll', handleScroll);
    }, [allCards, activeTitle]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/events/active-events/');
                const data = await response.json();
                setEvents(data);
                if (data.length > 0) {
                    setActiveTitle('Eventos');
                }
            } catch (error) {
                console.error('Error fetching events:', error);
                toast.error('No se pudieron cargar los eventos.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, []);

    const scrollToCard = (index) => {
        if (!scrollRef.current) return;
        const carousel = scrollRef.current;
        const items = carousel.querySelectorAll('.snap-center');
        const clamped = Math.max(0, Math.min(index, items.length - 1));
        const target = items[clamped];
        if (!target) return;
        // Center the target card within the carousel viewport
        const targetLeft = target.offsetLeft - (carousel.offsetWidth - target.offsetWidth) / 2;
        carousel.scrollTo({ left: targetLeft, behavior: 'smooth' });
    };

    const handleEventClick = (event) => {
        if (event.simplified_register) {
            navigate(`/events/counseling/simplified?eventId=${event.id}`);
        } else if (event.additional_info_required) {
            toast.loading('¡Espera un momento, estamos preparando algo especial! Pero antes, unas preguntas rápidas. 😬', {
                duration: 2000,
                position: 'bottom-center',
            });
            setTimeout(() => {
                navigate(`/events/counseling?eventId=${event.id}`);
            }, 1000);
        } else {
            navigate(`/events/${event.id}`);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="relative flex flex-col items-center w-full min-h-screen bg-[#fff5f5] overflow-hidden px-6 pb-24"
        >
            {/* Background elements (blurred circles) */}
            <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[120px] opacity-15 animate-slower-pulse animate-float" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[120px] opacity-15 animate-slower-pulse animate-float" style={{ animationDelay: '4s' }} />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[#f8485e] rounded-full mix-blend-multiply filter blur-[150px] opacity-10 animate-slow-pulse" />

            <div className="relative z-10 w-full flex flex-col items-center">
                <Logo />

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="w-full max-w-6xl mt-8"
                >
                    <div className="h-12 mb-8 px-2 overflow-hidden flex items-center">
                        <AnimatePresence mode="wait">
                            <motion.h1 
                                key={activeTitle}
                                initial={{ opacity: 0, y: 10, filter: 'blur(8px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                exit={{ opacity: 0, y: -10, filter: 'blur(8px)' }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="text-4xl font-extrabold text-[#f8485e] drop-shadow-sm uppercase"
                            >
                                {activeTitle}
                            </motion.h1>
                        </AnimatePresence>
                    </div>

                    {/* Carousel Container */}
                    <div className="relative min-h-[480px] w-full mt-4">
                        <AnimatePresence>
                            {isLoading ? (
                                <motion.div 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 flex items-center justify-center z-20"
                                >
                                    <Loader text="Buscando eventos y servicios..." />
                                </motion.div>
                            ) : (
                                <motion.div 
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="w-full"
                                >
                                    <div 
                                        ref={scrollRef}
                                        className="flex space-x-6 overflow-x-auto pb-8 scrollbar-hide snap-x snap-mandatory px-2"
                                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                    >
                                        {/* Render all cards */}
                                        {allCards.map((card, index) => (
                                            <div key={card.id || index} className="snap-center">
                                                <Card 
                                                    title={card.title}
                                                    label={card.label || (card.isEvent ? "EVENTO" : "SALUD")}
                                                    description={card.isEvent 
                                                        ? `🗓️ ${new Date(card.start_time).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}. Haz clic para registrarte.`
                                                        : card.description}
                                                    mediaUrl={card.image_url}
                                                    isFeatured={card.isEvent}
                                                    onClick={() => {
                                                        if (card.isEvent) {
                                                            handleEventClick(card);
                                                        } else if (card.isExternal) {
                                                            window.open(card.route, '_blank');
                                                        } else {
                                                            navigate(card.route);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Carousel navigation - Apple style (arrows + dots) */}
                    {!isLoading && allCards.length > 1 && (
                        <div className="flex items-center justify-center gap-5 mt-1">
                            <button
                                type="button"
                                onClick={() => scrollToCard(activeIndex - 1)}
                                disabled={activeIndex === 0}
                                aria-label="Anterior"
                                className="flex items-center justify-center w-11 h-11 rounded-full bg-white/70 backdrop-blur-md border border-[#f8485e]/15 text-[#f8485e] shadow-sm transition-all duration-200 hover:bg-white active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed disabled:active:scale-100"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>

                            {/* Dots */}
                            <div className="flex items-center gap-2">
                                {allCards.map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => scrollToCard(i)}
                                        aria-label={`Ir a la tarjeta ${i + 1}`}
                                        className={`rounded-full transition-all duration-300 ${
                                            i === activeIndex
                                                ? 'w-6 h-2 bg-[#f8485e]'
                                                : 'w-2 h-2 bg-[#f8485e]/30 hover:bg-[#f8485e]/50'
                                        }`}
                                    />
                                ))}
                            </div>

                            <motion.button
                                type="button"
                                onClick={() => scrollToCard(activeIndex + 1)}
                                disabled={activeIndex === allCards.length - 1}
                                aria-label="Siguiente"
                                animate={activeIndex === 0 ? { x: [0, 5, 0] } : { x: 0 }}
                                transition={activeIndex === 0 ? { repeat: Infinity, duration: 1.4, ease: 'easeInOut' } : {}}
                                className="flex items-center justify-center w-11 h-11 rounded-full bg-white/70 backdrop-blur-md border border-[#f8485e]/15 text-[#f8485e] shadow-sm transition-colors duration-200 hover:bg-white active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </motion.button>
                        </div>
                    )}
                </motion.div>

                {/* Disclaimer - Gestión de citas */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
                    className="w-full max-w-6xl mt-3 mb-0 px-2"
                >
                    <div className="flex items-center gap-2 bg-white/50 backdrop-blur-md border border-[#f8485e]/15 rounded-xl px-3 py-2 shadow-sm">
                        <span className="text-[#f8485e] text-sm flex-shrink-0">✉️</span>
                        <p className="text-[11px] text-gray-500 leading-snug">
                            <span className="font-bold text-gray-700">¿Cambiar o cancelar tu cita?</span>{' '}
                            Usa el enlace de tu correo de confirmación. Es la única forma segura.
                        </p>
                    </div>
                </motion.div>

                {/* Dona Button - Now in normal flow with premium loop animation */}
                <motion.div 
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ 
                        y: 0, 
                        opacity: 1,
                        scale: [1, 1.03, 1],
                    }}
                    transition={{ 
                        y: { delay: 1, type: 'spring' },
                        opacity: { delay: 1 },
                        scale: {
                            repeat: Infinity,
                            duration: 3,
                            ease: "easeInOut"
                        }
                    }}
                    className="mt-12 mb-12 flex justify-center w-full"
                >
                    <button
                        onClick={() => window.open('https://dona.inspiracambio.org/', '_blank')}
                        className="group relative bg-[#f8485e] text-white py-4 px-10 rounded-full shadow-2xl hover:bg-[#d93a4f] transition-all duration-300 active:scale-95 flex items-center space-x-3 font-bold text-lg overflow-hidden"
                    >
                        {/* Shimmer effect */}
                        <motion.div 
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                            animate={{
                                left: ['-100%', '200%'],
                            }}
                            transition={{
                                repeat: Infinity,
                                duration: 2.5,
                                ease: "easeInOut",
                                delay: 0.5
                            }}
                        />
                        
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            viewBox="0 0 24 24" 
                            fill="currentColor" 
                            className="w-6 h-6 transition-transform group-hover:scale-125 relative z-10"
                        >
                            <path d="m11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001Z" />
                        </svg>
                        <span className="relative z-10">Donar ahora</span>
                    </button>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ServicesPage;